import React, { useState, useEffect } from 'react';
import { X, Save, File, AlertTriangle, Upload, Calendar } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

interface Fichier {
  id: string;
  nom: string;
  type: 'document' | 'photo' | 'video' | 'audio' | 'autre';
  taille: string;
  date: string;
  description: string;
  url: string;
}

interface FichierModalProps {
  isOpen: boolean;
  onClose: () => void;
  fichier?: Fichier | null;
  onSave: (fichier: Omit<Fichier, 'id'>) => void;
  mode: 'add' | 'edit';
}

const FichierModal: React.FC<FichierModalProps> = ({ 
  isOpen, 
  onClose, 
  fichier, 
  onSave, 
  mode 
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    type: 'document' as 'document' | 'photo' | 'video' | 'audio' | 'autre',
    taille: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    url: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (fichier && mode === 'edit') {
      setFormData({
        nom: fichier.nom,
        type: fichier.type,
        taille: fichier.taille,
        date: fichier.date,
        description: fichier.description,
        url: fichier.url
      });
    } else {
      setFormData({
        nom: '',
        type: 'document',
        taille: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        url: ''
      });
    }
    setSelectedFile(null);
    setErrors({});
  }, [fichier, mode, isOpen]);

  // Gestion de la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom du fichier est requis';
    }
    
    if (mode === 'add' && !selectedFile) {
      newErrors.file = 'Veuillez sélectionner un fichier';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      if (mode === 'add' && selectedFile) {
        // Upload du fichier vers le serveur
        try {
          const formDataToUpload = new FormData();
          formDataToUpload.append('file', selectedFile);
          formDataToUpload.append('enqueteId', localStorage.getItem('currentEnqueteId') || '');
          formDataToUpload.append('description', formData.description);
          
          const response = await fetch(API_ENDPOINTS.UPLOAD_FILE, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formDataToUpload
          });
          
          if (response.ok) {
            const result = await response.json();
            onSave({
              ...formData,
              url: result.data.fichier.url,
              taille: `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`
            });
          } else {
            console.error('Erreur lors de l\'upload du fichier');
          }
        } catch (error) {
          console.error('Erreur lors de l\'upload:', error);
        }
      } else {
        // Mode édition
        onSave({
          ...formData,
          url: formData.url,
          taille: formData.taille
        });
      }
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData(prev => ({
        ...prev,
        nom: file.name,
        taille: `${(file.size / (1024 * 1024)).toFixed(1)} MB`
      }));
    }
  };

  const getFileTypeIcon = (type: string) => {
    switch (type) {
      case 'document': return '📄';
      case 'photo': return '📷';
      case 'video': return '🎥';
      case 'audio': return '🎵';
      default: return '📁';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }} onClick={onClose}>
      <div className="w-full max-w-2xl osint-card rounded-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-osint-light-gray">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-osint-orange rounded-lg flex items-center justify-center">
              <File className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Ajouter un fichier' : 'Modifier le fichier'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Upload */}
          {mode === 'add' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Sélectionner un fichier *
              </label>
              <div className="border-2 border-dashed border-osint-light-gray rounded-lg p-6 text-center">
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-16 h-16 bg-osint-light-gray rounded-lg flex items-center justify-center">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-400 text-sm">
                      Cliquez pour sélectionner un fichier
                    </p>
                    <p className="text-gray-500 text-xs">
                      Tous types de fichiers acceptés
                    </p>
                  </div>
                </label>
              </div>
              {errors.file && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  {errors.file}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nom du fichier *
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                className={`osint-input w-full ${errors.nom ? 'border-red-500' : ''}`}
                placeholder="Nom du fichier"
              />
              {errors.nom && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  {errors.nom}
                </p>
              )}
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Type de fichier
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                className="osint-input w-full"
              >
                <option value="document">Document</option>
                <option value="photo">Photo</option>
                <option value="video">Vidéo</option>
                <option value="audio">Audio</option>
                <option value="autre">Autre</option>
              </select>
            </div>

            {/* Taille */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Taille
              </label>
              <input
                type="text"
                value={formData.taille}
                onChange={(e) => setFormData(prev => ({ ...prev, taille: e.target.value }))}
                className="osint-input w-full"
                placeholder="2.5 MB"
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Date
              </label>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="osint-input flex-1"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="osint-input w-full h-24 resize-none"
              placeholder="Description du fichier..."
            />
          </div>

          {/* Aperçu du fichier */}
          {selectedFile && (
            <div className="p-4 bg-osint-light-gray rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{getFileTypeIcon(formData.type)}</span>
                <div className="flex-1">
                  <p className="text-white font-medium">{selectedFile.name}</p>
                  <p className="text-gray-400 text-sm">
                    {formData.taille} • {formData.type}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-osint-light-gray flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>{mode === 'add' ? 'Ajouter' : 'Modifier'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FichierModal; 